import { ReactNode, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"
import { CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from "recharts"
import GraphTemplate from "Components/templates/graph-template"
import ActionButton from "Components/atoms/buttons/action-button"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import Chip from "Components/atoms/chip"
import { PencilIcon, SyncIcon } from "Components/atoms/icons"
import Modal from "Components/molecule/modal"
import SelectInput from "Components/molecule/inputs/select-input"
import TextInput from "Components/molecule/inputs/text-input"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import TextAreaInput from "Components/molecule/inputs/textarea-inpuit"
import InfoTooltip from "Components/molecule/info-tooltip"
import LambdaRunStrip from "Components/molecule/lambda-run-strip"
import LambdaRunModal from "Components/organisms/lambda-run-modal"
import LambdaRunDialog from "Components/organisms/lambda-run-dialog"
import { LambdaRun, LambdaRunOptions, LambdaTrackerDetail, LambdaTrackerFields, LambdaTrackerUI } from "models/lambda-tracker"
import { setLambdaTrackerSettings } from "slices/admin/lambda-tracker/reducer"
import { getLambdaTrackerItem, runLambda, submitUpdateLambdaTracker } from "slices/admin/lambda-tracker/thunk"
import { formatDateTimeAmPm } from "utils/tools"
import withRouter from "Components/Common/withRouter"

const DAY_OPTIONS = [
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
    { value: "90", label: "Last 90 days" },
]

const CRON_EXAMPLES = "0 6 * * * = every day at 06:00 UTC · 0 */4 * * * = every 4 hours · 30 7 * * 1 = Mondays at 07:30 UTC"

const LambdaTrackerFormValidation = Yup.object({
    displayName: Yup.string().max(100),
    description: Yup.string().max(255),
    lambdaFunctionName: Yup.string().max(100),
    scheduleCron: Yup.string().max(100)
        .test("five-fields", "Use 5 fields: minute hour day-of-month month day-of-week (UTC)",
            v => !v || v.trim().split(/\s+/).length === 5),
    graceMinutes: Yup.number().typeError("Must be a number").integer().min(1).max(1440).required("Required"),
    syncButtonScreens: Yup.string().max(2000),
    rdsTables: Yup.string().max(4000),
    acumaticaGIs: Yup.string().max(2000),
    endpointUrl: Yup.string().max(255).matches(/^https:\/\/\S+$/i, { message: "Must be an https:// URL", excludeEmptyString: true }),
    notes: Yup.string().max(4000),
})

const STRIP_MAX_RUNS = 240

type Tone = "good" | "warning" | "bad" | "neutral"

const Kpi = ({ icon, label, value, caption, tone = "neutral", info }: {
    icon: string, label: string, value: ReactNode, caption?: ReactNode, tone?: Tone, info?: ReactNode
}) => (
    <div className={`kpi kpi-tone-${tone}`}>
        <label><i className={`mdi ${icon} me-1`} aria-hidden />{label}{info && <InfoTooltip>{info}</InfoTooltip>}</label>
        <strong>{value}</strong>
        {caption && <small>{caption}</small>}
    </div>
)

const LambdaDetailsPage = (props: any) => {
    const dispatch: any = useDispatch()
    const [searchParams] = useSearchParams()
    const id = searchParams.get("id")
    const initialDays = Number(searchParams.get("days")) || 30

    const [days, setDays] = useState<number>([7, 30, 90].includes(initialDays) ? initialDays : 30)
    const [detail, setDetail] = useState<LambdaTrackerDetail | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
    const [selectedRun, setSelectedRun] = useState<LambdaRun | null>(null)
    const [showEdit, setShowEdit] = useState<boolean>(false)
    const [showRun, setShowRun] = useState<boolean>(false)

    const item = detail?.item
    document.title = `${item?.displayName ?? "Lambda"} | Lambda Tracker | IbexIQ`;

    const load = async (windowDays: number) => {
        if (!id) return false
        const res = await getLambdaTrackerItem(id, windowDays)
        if (res) setDetail(res)
        return !!res
    }

    useEffect(() => {
        const init = async () => {
            if (!id) {
                toast.error("Lambda not specified.")
                props.router.navigate(-1)
                return
            }
            const ok = await load(days)
            if (!ok) {
                props.router.navigate(-1)
                return
            }
            setIsLoading(false)
        }
        init()
    }, [])

    const reload = async (windowDays = days) => {
        setIsRefreshing(true)
        await load(windowDays)
        setIsRefreshing(false)
    }

    const changeDays = (value: number) => {
        setDays(value)
        reload(value)
    }

    const formik = useFormik<LambdaTrackerFields>({
        enableReinitialize: true,
        initialValues: {
            displayName: item?.displayName ?? "",
            description: item?.description ?? "",
            lambdaFunctionName: item?.lambdaFunctionName ?? "",
            scheduleCron: item?.scheduleCron ?? "",
            graceMinutes: item?.graceMinutes ?? 60,
            isActive: item?.isActive ?? true,
            syncButtonScreens: item?.syncButtonScreens ?? "",
            rdsTables: item?.rdsTables ?? "",
            acumaticaGIs: item?.acumaticaGIs ?? "",
            endpointUrl: item?.endpointUrl ?? "",
            notes: item?.notes ?? "",
        },
        validationSchema: LambdaTrackerFormValidation,
        onSubmit: async (vals, helpers) => {
            if (!id || !item) return
            helpers.setSubmitting(true)
            const res = await submitUpdateLambdaTracker(id, vals)
            if (res) {
                dispatch(setLambdaTrackerSettings({
                    id,
                    displayName: res.displayName ?? item.code,
                    description: res.description ?? null,
                    lambdaFunctionName: res.lambdaFunctionName ?? null,
                    scheduleCron: res.scheduleCron ?? null,
                    graceMinutes: res.graceMinutes ?? item.graceMinutes,
                    isActive: res.isActive ?? item.isActive,
                    syncButtonScreens: res.syncButtonScreens ?? null,
                    rdsTables: res.rdsTables ?? null,
                    acumaticaGIs: res.acumaticaGIs ?? null,
                    endpointUrl: res.endpointUrl ?? null,
                    notes: res.notes ?? null,
                }))
                setShowEdit(false)
                // Missed / late runs depend on the schedule: recompute them.
                await reload()
            }
            helpers.setSubmitting(false)
        },
    })

    // Newest first for the table; chart uses chronological order.
    const runsDesc = useMemo(() => [...(item?.runs ?? [])].reverse(), [item])

    const chartData = useMemo(() => (item?.runs ?? [])
        .filter(r => r.status !== "Missed" && r.startAt)
        .map(r => ({
            time: new Date(r.startAt as string).getTime(),
            durationS: r.durationMs !== null ? Number((r.durationMs / 1000).toFixed(2)) : null,
            memoryMb: r.maxMemoryUsedMb,
            failedS: r.health === "red" && r.durationMs !== null ? Number((r.durationMs / 1000).toFixed(2)) : null,
        })), [item])

    const s = item?.stats
    const memoryRatio = s?.peakMemoryUsedMb && s?.memorySizeMb ? s.peakMemoryUsedMb / s.memorySizeMb : null
    const successTone: Tone = s?.successRate == null ? "neutral" : s.successRate >= 0.95 ? "good" : s.successRate >= 0.8 ? "warning" : "bad"
    const memoryTone: Tone = memoryRatio === null ? "neutral" : memoryRatio >= 0.9 ? "bad" : memoryRatio >= 0.75 ? "warning" : "good"
    const pickRun = (key: string | null | undefined) => key ? item?.runs.find(r => r.key === key) ?? null : null
    const bestRun = pickRun(s?.bestRunKey)
    const worstRun = pickRun(s?.worstRunKey)

    return (
        <GraphTemplate
            title="Lambda Tracker"
            breadcrumbItem={item?.displayName ?? "Lambda"}
            isLoading={isLoading}
            actions={
                <div className="d-flex gap-2">
                    {item?.runnable && (
                        <ActionButton onClick={() => setShowRun(true)} disabled={item.manualRunActive}
                            title={item.manualRunActive ? "A manual run of this Lambda is in progress" : "Run this Lambda now"}>
                            {item.manualRunActive ? "Running…" : "Run now"} <i className="mdi mdi-play" />
                        </ActionButton>
                    )}
                    <ActionButton onClick={() => setShowEdit(true)} disabled={!item}>Edit <PencilIcon /></ActionButton>
                    <ActionButton onClick={() => reload()} disabled={isRefreshing}>{isRefreshing ? "Refreshing…" : "Refresh"} <SyncIcon /></ActionButton>
                </div>
            }
            filters={
                <div className="lambda-tracker-filters">
                    <SelectInput label="Period" options={DAY_OPTIONS} value={String(days)}
                        onChange={e => changeDays(Number(e.target.value))} disabled={isRefreshing} />
                </div>
            }
            modal={<>
                <LambdaRunModal run={selectedRun} lambdaName={item?.displayName ?? ""}
                    functionName={item?.functionName ?? null} onClose={() => setSelectedRun(null)} />
                {item && showRun && (
                    <LambdaRunDialog isOpen={showRun} onClose={() => setShowRun(false)}
                        target={{ mode: "single", name: item.displayName, kind: item.runKind }}
                        onSubmit={async (options: LambdaRunOptions) => {
                            const batch = await runLambda(item.id, options)
                            if (batch) await reload()
                            return !!batch
                        }} />
                )}
                <Modal isOpen={showEdit} onClose={() => { setShowEdit(false); formik.resetForm() }}
                    title="Edit Lambda" subtitle={item?.code}>
                    <form onSubmit={formik.handleSubmit} className="d-flex flex-column gap-3">
                        <TextInput label="Display name" name="displayName" value={formik.values.displayName}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.displayName && formik.touched.displayName} message={formik.touched.displayName ? formik.errors.displayName : undefined} />
                        <TextInput label="Description" name="description" value={formik.values.description}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.description && formik.touched.description} message={formik.touched.description ? formik.errors.description : undefined} />
                        <TextInput label="AWS Lambda function name" name="lambdaFunctionName" value={formik.values.lambdaFunctionName}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            message={formik.errors.lambdaFunctionName ?? "Used to read /aws/lambda/<name> in CloudWatch Logs"} />
                        <TextInput label="Fallback schedule (cron, UTC)" name="scheduleCron" placeholder="0 6 * * *" value={formik.values.scheduleCron}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.scheduleCron && formik.touched.scheduleCron}
                            message={formik.touched.scheduleCron && formik.errors.scheduleCron ? formik.errors.scheduleCron : `Used when EventBridge has no schedule for this Lambda or cannot be read. ${CRON_EXAMPLES}`} />
                        <TextInput label="Grace period (minutes)" name="graceMinutes" type="number" min={1} max={1440} value={formik.values.graceMinutes}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.graceMinutes && formik.touched.graceMinutes}
                            message={formik.touched.graceMinutes && formik.errors.graceMinutes ? String(formik.errors.graceMinutes) : "How long after the scheduled time a run still counts as on time"} />
                        <TextAreaInput label="Sync button on screens" name="syncButtonScreens" max={2000} rows={2} value={formik.values.syncButtonScreens}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.syncButtonScreens && formik.touched.syncButtonScreens} message={formik.touched.syncButtonScreens && formik.errors.syncButtonScreens ? formik.errors.syncButtonScreens : "Screens with a Sync button that runs this Lambda; empty = none"} />
                        <TextAreaInput label="Tables in RDS updated" name="rdsTables" max={4000} rows={2} value={formik.values.rdsTables}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.rdsTables && formik.touched.rdsTables} message={formik.touched.rdsTables && formik.errors.rdsTables ? formik.errors.rdsTables : undefined} />
                        <TextAreaInput label="Acumatica GIs" name="acumaticaGIs" max={2000} rows={2} value={formik.values.acumaticaGIs}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.acumaticaGIs && formik.touched.acumaticaGIs} message={formik.touched.acumaticaGIs && formik.errors.acumaticaGIs ? formik.errors.acumaticaGIs : undefined} />
                        <TextInput label="Endpoint URL" name="endpointUrl" placeholder="https://..." value={formik.values.endpointUrl}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.endpointUrl && formik.touched.endpointUrl}
                            message={formik.touched.endpointUrl && formik.errors.endpointUrl ? formik.errors.endpointUrl : "Lambda function URL. Stored only in this environment's database"} />
                        <TextAreaInput label="Notes" name="notes" max={4000} rows={2} value={formik.values.notes}
                            onChange={formik.handleChange} onBlur={formik.handleBlur}
                            error={!!formik.errors.notes && formik.touched.notes} message={formik.touched.notes && formik.errors.notes ? formik.errors.notes : undefined} />
                        <CheckboxInput label="Active (inactive Lambdas are never flagged as missed)" name="isActive"
                            checked={formik.values.isActive} onChange={formik.handleChange} />
                        <div className="d-flex justify-content-end gap-2">
                            <SecondaryButton onClick={() => { setShowEdit(false); formik.resetForm() }}>Cancel</SecondaryButton>
                            <PrimaryButton onClick={() => formik.handleSubmit()} disabled={formik.isSubmitting}>
                                {formik.isSubmitting ? "Saving…" : "Save"}
                            </PrimaryButton>
                        </div>
                    </form>
                </Modal>
            </>}
        >
            {item && s && (
                <div className={`lambda-tracker lambda-details ${isRefreshing ? "is-refreshing" : ""}`}>
                    <div className={`health-banner border-${LambdaTrackerUI.chipVariant(item.currentHealth)}`}>
                        <div>
                            <div className="headline">
                                <i className={`${LambdaTrackerUI.healthIcon(item.currentHealth)} health-${item.currentHealth ?? "none"}`} aria-hidden />
                                <div>
                                    <h4>{item.displayName}</h4>
                                    <small className="text-muted">{item.description ?? item.code}</small>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                <Chip variant={LambdaTrackerUI.chipVariant(item.currentHealth)}>{LambdaTrackerUI.healthLabel(item.currentHealth)}</Chip>
                                <Chip variant="secondary">{item.externalSystem}</Chip>
                                {!item.isActive && <Chip variant="secondary">Inactive</Chip>}
                            </div>
                        </div>
                        <div className="meta">
                            <small>Function: <code>{item.functionName ?? item.lambdaFunctionName ?? "not set"}</code></small>
                            <small>Code: <code>{item.code}</code></small>
                            <small>Schedule: {LambdaTrackerUI.schedules(item)} (+{item.graceMinutes} min grace)</small>
                            {item.nextExpectedAt && <small>Next expected: {formatDateTimeAmPm(item.nextExpectedAt)}</small>}
                            <small>Last sync: {formatDateTimeAmPm(item.lastSync)}</small>
                        </div>
                    </div>

                    {!detail.cloudWatch.available && (
                        <div className="alert alert-warning mb-0" role="alert">
                            <i className="mdi mdi-cloud-alert me-1" aria-hidden />
                            CloudWatch Logs could not be read: billed duration and cost are estimated, and timeouts or crashes without a SyncLog row are not shown.
                            <small className="d-block text-muted">{detail.cloudWatch.errors.join(" · ")}</small>
                        </div>
                    )}

                    <div className="health-kpis">
                        <Kpi icon="mdi-check-decagram" label="Success rate" tone={successTone}
                            value={LambdaTrackerUI.percent(s.successRate)}
                            caption={`${s.okCount} ok · ${s.warningCount} warning · ${s.errorCount} failed · ${s.missedCount} missed`}
                            info="Successful runs divided by all runs plus missed scheduled runs." />
                        <Kpi icon="mdi-rocket-launch" label="Best time" tone="good"
                            value={LambdaTrackerUI.duration(s.bestMs)}
                            caption={bestRun ? <button type="button" className="btn btn-link p-0 small" onClick={() => setSelectedRun(bestRun)}>{formatDateTimeAmPm(bestRun.startAt)}</button> : "-"} />
                        <Kpi icon="mdi-turtle" label="Worst time" tone={s.worstMs && s.p95Ms && s.worstMs > s.p95Ms * 2 ? "warning" : "neutral"}
                            value={LambdaTrackerUI.duration(s.worstMs)}
                            caption={worstRun ? <button type="button" className="btn btn-link p-0 small" onClick={() => setSelectedRun(worstRun)}>{formatDateTimeAmPm(worstRun.startAt)}</button> : "-"} />
                        <Kpi icon="mdi-timer-outline" label="Average / p95"
                            value={LambdaTrackerUI.duration(s.avgMs)}
                            caption={`p95 ${LambdaTrackerUI.duration(s.p95Ms)}`}
                            info="Timed-out and crashed runs are left out of the timing figures." />
                        <Kpi icon="mdi-memory" label="Peak memory" tone={memoryTone}
                            value={LambdaTrackerUI.memory(s.peakMemoryUsedMb)}
                            caption={<>
                                of {LambdaTrackerUI.memory(s.memorySizeMb)} configured{memoryRatio !== null && ` (${LambdaTrackerUI.percent(memoryRatio)})`} · avg {LambdaTrackerUI.memory(s.avgMemoryUsedMb)}
                                {memoryRatio !== null && <div className="kpi-meter"><div style={{ width: `${Math.min(100, memoryRatio * 100)}%` }} /></div>}
                            </>} />
                        <Kpi icon="mdi-currency-usd" label="Cost in period"
                            value={LambdaTrackerUI.cost(s.totalCostUsd)}
                            caption={`${LambdaTrackerUI.cost(s.avgCostPerRunUsd)} per run`}
                            info="GB-seconds × on-demand x86 price + request price. Runs without a CloudWatch REPORT line are estimated from SyncLog times." />
                        <Kpi icon="mdi-calendar-month" label="Projected monthly cost"
                            value={LambdaTrackerUI.cost(s.projectedMonthlyCostUsd)}
                            caption={`Based on the last ${days} days`} />
                        <Kpi icon="mdi-history" label="Last success"
                            tone={s.lastSuccessAt ? "neutral" : "bad"}
                            value={s.lastSuccessAt ? formatDateTimeAmPm(s.lastSuccessAt) : "None in period"}
                            caption={`${s.totalRuns.toLocaleString()} runs in period · ${s.errors7d} errors in 7 days`} />
                    </div>

                    <section>
                        <h5 className="section-title">Info</h5>
                        <dl className="lambda-info">
                            <dt>Schedules ({item.scheduleSource === "eventbridge" ? "EventBridge" : item.scheduleSource === "database" ? "saved in IbexIQ" : "none"})</dt>
                            <dd>
                                {item.schedules.length === 0 && "-"}
                                {item.schedules.map(sc => (
                                    <div key={sc.name}>
                                        <code>{sc.expression}</code> {sc.timezone ?? "UTC"} · {sc.enabled ? "enabled" : <span className="text-danger">disabled</span>}
                                        <small className="text-muted"> · {sc.name}{sc.input ? <> · input <code>{sc.input}</code></> : null}</small>
                                    </div>
                                ))}
                                <small className="text-muted">Grace {item.graceMinutes} min</small>
                            </dd>
                            <dt>Manual run</dt><dd>{item.runnable ? LambdaTrackerUI.runKindHelp(item.runKind) : item.runKind === "trigger" ? "Sends emails: runs only on its schedule." : "Not available in this environment."}</dd>
                            <dt>Sync button</dt><dd>{item.syncButtonScreens ?? "No"}</dd>
                            <dt>Tables in RDS updated</dt><dd>{item.rdsTables ?? "-"}</dd>
                            <dt>Acumatica GIs</dt><dd>{item.acumaticaGIs ?? "-"}</dd>
                            <dt>Endpoint</dt><dd>{item.endpointUrl ? <a href={item.endpointUrl} target="_blank" rel="noreferrer">{item.endpointUrl}</a> : "-"}</dd>
                            <dt>Screens that depend on it</dt><dd>{item.dependentScreens.length ? item.dependentScreens.map(d => d.name).join(", ") : "-"}</dd>
                            <dt>Billed (avg) / rate per ms</dt><dd>{LambdaTrackerUI.duration(s.avgBilledMs)} · {LambdaTrackerUI.rate(s.ratePerMsUsd)}</dd>
                            <dt>Cost per month (schedule)</dt><dd>{LambdaTrackerUI.cost(s.monthlyCostByScheduleUsd)}{s.runsPerMonthBySchedule !== null && <> · {s.runsPerMonthBySchedule.toLocaleString()} runs</>}</dd>
                            <dt>Notes</dt><dd className="notes">{item.notes ?? "-"}</dd>
                        </dl>
                    </section>

                    <section>
                        <h5 className="section-title">Run history {item.runsInWindow > item.runs.length && <small className="text-muted fw-normal">(latest {item.runs.length} of {item.runsInWindow.toLocaleString()})</small>}</h5>
                        <LambdaRunStrip runs={item.runs} max={STRIP_MAX_RUNS} wrap onSelect={setSelectedRun} />
                    </section>

                    {chartData.length > 0 && (
                        <section>
                            <h5 className="section-title">Duration and memory</h5>
                            <div style={{ width: "100%", height: 320 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chartData} margin={{ right: 10, left: 10, bottom: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--bs-border-color)" />
                                        <XAxis dataKey="time" type="number" scale="time" domain={["dataMin", "dataMax"]}
                                            tickFormatter={(v: number) => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                            stroke="var(--bs-secondary-color)" />
                                        <YAxis yAxisId="duration" stroke="var(--bs-secondary-color)"
                                            label={{ value: "Seconds", angle: -90, position: "insideLeft", fill: "var(--bs-secondary-color)" }} />
                                        <YAxis yAxisId="memory" orientation="right" stroke="var(--bs-secondary-color)"
                                            label={{ value: "MB", angle: 90, position: "insideRight", fill: "var(--bs-secondary-color)" }} />
                                        <Tooltip
                                            labelFormatter={(v: number) => formatDateTimeAmPm(new Date(v))}
                                            formatter={(value: number, name: string) => name === "Memory used" ? `${value} MB` : `${value} s`}
                                            contentStyle={{ background: "var(--bs-body-bg)", borderColor: "var(--bs-border-color)" }} />
                                        <Legend />
                                        <Line yAxisId="duration" dataKey="durationS" name="Duration" stroke="var(--health-series-actual)" dot={{ r: 2 }} connectNulls isAnimationActive={false} />
                                        <Line yAxisId="memory" dataKey="memoryMb" name="Memory used" stroke="var(--health-series-planned)" strokeDasharray="4 3" dot={false} connectNulls isAnimationActive={false} />
                                        <Scatter yAxisId="duration" dataKey="failedS" name="Failed run" fill="var(--bs-danger)" isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </section>
                    )}

                    <section>
                        <h5 className="section-title">Runs <span className="text-muted">({item.runsInWindow > runsDesc.length ? `latest ${runsDesc.length} of ${item.runsInWindow.toLocaleString()}` : runsDesc.length})</span></h5>
                        <div className="table-responsive lambda-runs-table">
                            <table className="table table-sm table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Started</th>
                                        <th>Duration</th>
                                        <th>Billed</th>
                                        <th>Memory</th>
                                        <th>Cost</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runsDesc.length === 0 && (
                                        <tr><td colSpan={7} className="text-center text-muted py-4">No runs in this period.</td></tr>
                                    )}
                                    {runsDesc.map(run => (
                                        <tr key={run.key} onClick={() => setSelectedRun(run)} style={{ cursor: "pointer" }}>
                                            <td>
                                                <Chip variant={LambdaTrackerUI.chipVariant(run.health)}>
                                                    <i className={`${LambdaTrackerUI.icon(run)} me-1`} aria-hidden />
                                                    {LambdaTrackerUI.statusLabel(run.status)}
                                                </Chip>
                                            </td>
                                            <td>{formatDateTimeAmPm(LambdaTrackerUI.runTime(run))}</td>
                                            <td>{LambdaTrackerUI.duration(run.durationMs)}</td>
                                            <td>{LambdaTrackerUI.duration(run.billedDurationMs)}</td>
                                            <td>{run.maxMemoryUsedMb !== null ? `${LambdaTrackerUI.memory(run.maxMemoryUsedMb)} / ${LambdaTrackerUI.memory(run.memorySizeMb)}` : "-"}</td>
                                            <td>{LambdaTrackerUI.cost(run.costUsd)}{run.estimated && run.costUsd !== null && <span className="text-muted" title="Estimated"> *</span>}</td>
                                            <td className="lambda-run-reason-cell" title={run.errorMessage ?? run.reason ?? ""}>{run.reason ?? ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <small className="text-muted">* Estimated from the SyncLog start and end times (no CloudWatch REPORT line).</small>
                    </section>
                </div>
            )}
        </GraphTemplate>
    )
}

export default withRouter(LambdaDetailsPage)
